// ---------------------------------------------------------------------------
// F-022 Phase 2 — Email Subject AI Scoring CQRS Event Catalog (vocabulary)
// ---------------------------------------------------------------------------
// Feature F-022: "AI-assisted email subject scoring." Per audit:
// NO HTTP endpoint surfaces this capability — scoring runs inline inside
// the email-compose / marketing-campaign pipeline and emits CQRS events
// for audit, training, and operator-facing dashboards. The audit
// explicitly excluded F-022 from the mounted-route registry because the
// scorer is invoked by `packages/email-marketing` send-side code paths
// rather than a dedicated `/subject-score` route.
//
// Per CLAUDE.md Governance Rule #7 ("Event Catalog"): every event type
// MUST be catalogued BEFORE any implementation lands. This Phase 2
// narrow follow-up pins the closed vocabulary the future subject-AI
// scorer (and its downstream A/B variant suggester + policy gate) MUST
// emit so:
//
//   - The scorer service emits a single canonical event-type string for
//     every scored subject — no hand-rolled drift across email-compose,
//     marketing-campaign, and webinar-invite call sites.
//   - The variant suggester emits a separate atomic event (Governance
//     Rule #6 — one fact per event) carrying the predicted lift in basis
//     points so the A/B engine can replay the suggestion ledger.
//   - The policy gate emits a `blocked` event with a closed reason vocab
//     so DSAR / compliance review can trace every refusal back to a
//     stable taxonomy.
//
// Pure builders only. No I/O. No DB writes. No env access. ISO-8601
// canonical timestamps (offset-aware via `z.string().datetime({ offset:
// true })`). The writer (email-compose route handler, campaign worker,
// or webinar-invite worker) owns actual emission to `customer_events`
// + the `event-router` worker.
//
// Mirrors the BIL-017 / ADS-005 / INB-014 Phase 2 vocabulary-first
// pattern already shipped under `packages/db/src/schema/`.
// ---------------------------------------------------------------------------

import { z } from "zod";

// ---------------------------------------------------------------------------
// Event type catalog — frozen string constants
// ---------------------------------------------------------------------------

export const F_022_SUBJECT_SCORED_EVENT = "email.subject_scored@v1" as const;
export const F_022_SUBJECT_VARIANT_SUGGESTED_EVENT =
  "email.subject_variant_suggested@v1" as const;
export const F_022_SUBJECT_BLOCKED_EVENT = "email.subject_blocked@v1" as const;

export const F_022_SUBJECT_AI_EVENT_TYPES = [
  F_022_SUBJECT_SCORED_EVENT,
  F_022_SUBJECT_VARIANT_SUGGESTED_EVENT,
  F_022_SUBJECT_BLOCKED_EVENT,
] as const;

export type F022SubjectAiEventType =
  (typeof F_022_SUBJECT_AI_EVENT_TYPES)[number];

export function isF022SubjectAiEventType(
  value: unknown,
): value is F022SubjectAiEventType {
  return (
    typeof value === "string" &&
    (F_022_SUBJECT_AI_EVENT_TYPES as readonly string[]).includes(value)
  );
}

// ---------------------------------------------------------------------------
// Vocabulary enums — closed sets so consumers `switch` exhaustively
// ---------------------------------------------------------------------------

/**
 * Closed catalog of refusal reasons the policy gate may attach to a
 * `subject_blocked` event. Two values intentionally — narrow taxonomy
 * keeps downstream DSAR / compliance review trivial. Future expansion
 * MUST go through a new `@v2` event type rather than widening this
 * vocab in place (drift guard).
 *
 *  - `spam_risk` — the spam-classifier confidence exceeded the policy
 *    threshold (e.g. RAW LIKELIHOOD > 0.85) and the subject would harm
 *    sender reputation if delivered.
 *  - `policy_violation` — the subject violated the tenant's content
 *    policy (banned keywords, regulated-industry rules, GDPR-flagged
 *    PII surfacing in subject line).
 */
export const F_022_SUBJECT_BLOCK_REASON_VOCAB = [
  "spam_risk",
  "policy_violation",
] as const;

export type F022SubjectBlockReason =
  (typeof F_022_SUBJECT_BLOCK_REASON_VOCAB)[number];

export function isF022SubjectBlockReason(
  value: unknown,
): value is F022SubjectBlockReason {
  return (
    typeof value === "string" &&
    (F_022_SUBJECT_BLOCK_REASON_VOCAB as readonly string[]).includes(value)
  );
}

// ---------------------------------------------------------------------------
// Shared base — every event carries these
// ---------------------------------------------------------------------------

export interface F022SubjectAiEventBase {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** ISO-8601 (offset-aware) — when the event was emitted. */
  occurredAtIso: string;
  /** Correlation id joining scored / variant / blocked across one compose flow. */
  correlationId: string;
}

// ---------------------------------------------------------------------------
// Per-event Zod schemas + inferred payload types
// ---------------------------------------------------------------------------

/**
 * `email.subject_scored@v1` — the AI scorer evaluated a subject line and
 * produced an open-probability estimate in basis points (0..10000). One
 * event per (correlationId, subjectLine) — re-scoring the same subject
 * in the same compose session MUST reuse the correlation id and emit a
 * new event (the model version may have changed).
 */
export const F022SubjectScoredPayloadSchema = z.object({
  type: z.literal(F_022_SUBJECT_SCORED_EVENT),
  tenantId: z.string().min(1),
  subjectLine: z.string().min(1).max(998),
  /** Open probability in basis points (0..10000 inclusive; 10000 = 100.00%). */
  openProbabilityBps: z.number().int().min(0).max(10000),
  /** Scoring model identifier (e.g. "subject-scorer-v3", "gpt-4o-mini@2026-05"). */
  model: z.string().min(1).max(128),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});

export type F022SubjectScoredPayload = z.infer<
  typeof F022SubjectScoredPayloadSchema
>;

/**
 * `email.subject_variant_suggested@v1` — the variant generator proposed
 * an A/B alternative to a previously scored subject. The predicted lift
 * is in basis points (signed; a negative lift indicates a "tested-and-
 * rejected" variant kept on the audit trail for model training).
 */
export const F022SubjectVariantSuggestedPayloadSchema = z.object({
  type: z.literal(F_022_SUBJECT_VARIANT_SUGGESTED_EVENT),
  tenantId: z.string().min(1),
  originalSubject: z.string().min(1).max(998),
  suggestion: z.string().min(1).max(998),
  /** Predicted lift over baseline in basis points (signed: +100 = +1.00pp). */
  predictedLiftBps: z.number().int().min(-10000).max(10000),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});

export type F022SubjectVariantSuggestedPayload = z.infer<
  typeof F022SubjectVariantSuggestedPayloadSchema
>;

/**
 * `email.subject_blocked@v1` — policy gate refused the subject line.
 * Carries a closed `reason` vocab so DSAR / compliance review can trace
 * every refusal back to a stable taxonomy. Refusal does NOT delete the
 * scored event; it appends a `blocked` audit row sharing the same
 * correlation id.
 */
export const F022SubjectBlockedPayloadSchema = z.object({
  type: z.literal(F_022_SUBJECT_BLOCKED_EVENT),
  tenantId: z.string().min(1),
  subjectLine: z.string().min(1).max(998),
  reason: z.enum(F_022_SUBJECT_BLOCK_REASON_VOCAB),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});

export type F022SubjectBlockedPayload = z.infer<
  typeof F022SubjectBlockedPayloadSchema
>;

// ---------------------------------------------------------------------------
// Discriminated union — consumers `switch (event.type)` for exhaustive narrow
// ---------------------------------------------------------------------------

export type F022SubjectAiEvent =
  | F022SubjectScoredPayload
  | F022SubjectVariantSuggestedPayload
  | F022SubjectBlockedPayload;

// ---------------------------------------------------------------------------
// Pure builders — accept caller primitives, return typed envelope
// ---------------------------------------------------------------------------

function assertNonEmptyString(value: string, field: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`F-022 subject-ai emitter: ${field} must be non-empty string`);
  }
  return value;
}

function nowIso(now: Date | undefined): string {
  return (now ?? new Date()).toISOString();
}

export interface BuildSubjectScoredInput {
  tenantId: string;
  subjectLine: string;
  openProbabilityBps: number;
  model: string;
  correlationId: string;
  now?: Date;
}

export function buildSubjectScoredEvent(
  input: BuildSubjectScoredInput,
): F022SubjectScoredPayload {
  return F022SubjectScoredPayloadSchema.parse({
    type: F_022_SUBJECT_SCORED_EVENT,
    tenantId: assertNonEmptyString(input.tenantId, "tenantId"),
    subjectLine: assertNonEmptyString(input.subjectLine, "subjectLine"),
    openProbabilityBps: input.openProbabilityBps,
    model: assertNonEmptyString(input.model, "model"),
    occurredAtIso: nowIso(input.now),
    correlationId: assertNonEmptyString(input.correlationId, "correlationId"),
  });
}

export interface BuildSubjectVariantSuggestedInput {
  tenantId: string;
  originalSubject: string;
  suggestion: string;
  predictedLiftBps: number;
  correlationId: string;
  now?: Date;
}

export function buildSubjectVariantSuggestedEvent(
  input: BuildSubjectVariantSuggestedInput,
): F022SubjectVariantSuggestedPayload {
  return F022SubjectVariantSuggestedPayloadSchema.parse({
    type: F_022_SUBJECT_VARIANT_SUGGESTED_EVENT,
    tenantId: assertNonEmptyString(input.tenantId, "tenantId"),
    originalSubject: assertNonEmptyString(
      input.originalSubject,
      "originalSubject",
    ),
    suggestion: assertNonEmptyString(input.suggestion, "suggestion"),
    predictedLiftBps: input.predictedLiftBps,
    occurredAtIso: nowIso(input.now),
    correlationId: assertNonEmptyString(input.correlationId, "correlationId"),
  });
}

export interface BuildSubjectBlockedInput {
  tenantId: string;
  subjectLine: string;
  reason: F022SubjectBlockReason;
  correlationId: string;
  now?: Date;
}

export function buildSubjectBlockedEvent(
  input: BuildSubjectBlockedInput,
): F022SubjectBlockedPayload {
  return F022SubjectBlockedPayloadSchema.parse({
    type: F_022_SUBJECT_BLOCKED_EVENT,
    tenantId: assertNonEmptyString(input.tenantId, "tenantId"),
    subjectLine: assertNonEmptyString(input.subjectLine, "subjectLine"),
    reason: input.reason,
    occurredAtIso: nowIso(input.now),
    correlationId: assertNonEmptyString(input.correlationId, "correlationId"),
  });
}
